import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  Heading,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import NextLink from 'next/link';
import { getPublicRuntimeConfig } from '../common/config';
import { SortOrder } from '../common/libs/sortOrder';
import useSlidePostBoostArticles from '../hooks/useSlidePostBoostArticles';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { SPECIAL_PAGE_GA_CATEGORY } from '../common/constants';
import { generateS3DownloadUrl } from '../server/libs/siteSettings';
import FullWidthBox from './FullWidthBox';
import ArticlesGrid from './ArticlesGrid';
import { Props as ArticleHeader } from './ArticleSummary';
import ViewLikeCount, { calcArticlesViewLineCounts } from './ViewLikeCount';
import { XPostWidget } from './XPostWidget';
import SlideSortButtons from './SlideSortButtons';
import { HeroAreaSettings, HeroArea } from './HeroArea';
import SpecialPageLinks, { SpecialPageLinkItem } from './SpecialPageLinks';
import { MessageAreaSettings, MessageArea } from './MessageArea';
import SpecialPageDescription, {
  DescriptionSettings,
} from './SpecialPageDescription';

const { qaWebBaseUrl, slideWebBaseUrl } = getPublicRuntimeConfig();
// アンター共通リンクバナーリストのデフォルト値
const defaultCommonLinks: SpecialPageLinkItem[] = [
  {
    title: 'Antaa Slideテンプレート集',
    image: '/images/slide_template.png',
    link: `${qaWebBaseUrl}/auth/redirect?type=slide-template`,
  },
  {
    title: '実例とテンプレから学ぶ！スライド作成完全ガイド',
    image: '/images/docs_academy_banner_936x238.png',
    link: `${slideWebBaseUrl}/docs/academy`,
  },
  {
    title: 'Antaa Slide 書籍化までの道のり',
    image: '/images/shosekika_banner_936_240.png',
    link: `${slideWebBaseUrl}/docs/shosekika`,
  },
];

/**
 * S3バケットに保存する"post-boost"テンプレート専用JSONデータの型
 */
export type SlidePostBoosSettings = {
  templateName: 'post-boost';
  headLogo?: {
    image?: string; // ロゴ画像パス（imageS3Keyよりも優先される）
    imageS3Key?: string; // ロゴ画像S3キー
    bgColor: string;
    maxHeight?: number;
  };
  heroContent: HeroAreaSettings;
  /*
  boostMessageがない場合は投稿ボタン非表示
   */
  boostMessage?: {
    layout?: 'default' | 'with-profile';
    title?: string;
    text?: string; // markdown形式の説明文
    buttonHeader?: string | null;
    tags?: string[];
    customUploadGaLabel?: string; // 投稿ボタンクリック時のgaイベントラベル名で後方互換性などの目的でイレギュラーな名前を指定する場合に利用
    iconImageS3Key?: string | null; // 寄稿者の画像S3キー
    caption?: string | string[] | null; // 寄稿者の名前/所属
    alwaysShowBoostButton?: boolean; // 投稿ボタンを常に表示するかどうか
  };
  description: DescriptionSettings;
  message?: MessageAreaSettings;
  /**
   * Antaa関連リンク
   *   特集ページを閲覧できる全てのアカウントタイプで表示される
   *   undefinedとした場合はデフォルトのリンク（defaultCommonLinks）が表示される。
   *   空の配列を設定するとこのスペース自体が非表示になる。
   */
  links?: SpecialPageLinkItem[] | undefined;
  /**
   * 企業関連リンクバナー
   */
  corporationBanners:
    | {
        banners: SpecialPageLinkItem[];
        grid?: [number, number, number]; // バナーの数が多い時のレイアウト調整としてcss gridのレスポンシブ値
        showToNoLoginUsers?: boolean; // trueの場合 非ログインユーザーを含む全ユーザーに表示（falsyの時は認証済み医師アカウント/許可アカウントのみに表示）
      }
    | SpecialPageLinkItem[] // 旧設定値と互換性確保のため
    | null;
  corporationBannersGrid?: [number, number, number]; //  corporationBanners gridのレスポンシブ値（旧設定値と互換性確保のため）
  /**
   * 企業関連記事へのバナー
   */
  corporationExternalArticles: {
    listTitle: string;
    articles: SpecialPageLinkItem[];
    grid?: [number, number, number]; // バナーの数が多い時のレイアウト調整としてcss gridのレスポンシブ値
    showToNoLoginUsers?: boolean; // trueの場合 非ログインユーザーを含む全ユーザーに表示（falsyの時は認証済み医師アカウント/許可アカウントのみに表示）
  } | null;
  // 企業関連antaaスライド一覧
  corporationArticles: {
    listTitle: string;
    articleIds: string[];
    articles?: ArticleHeader[];
  } | null;
  articles: {
    // 指定したタグがつけられたスライド取得
    tags?: string[];
    // 指定したidのスライド取得
    articleIds: string[];
    // start-endの期間に初めて公開されたスライドを取得
    firstPublishedPeriod?: {
      start: string; // ISO8601
      end?: string; // ISO8601
    };
    listTitle: string | null;
    articles?: ArticleHeader[];
    sortOrder: SortOrder;
    onClickSortOrder?: (sortOrder: SortOrder) => void;
  };
  hideArticlesSortButtons?: boolean;
  hideArticlesCounts?: boolean;
  xPostIds?: string[];
};

export type SlidePostBoostContentProps = SlidePostBoosSettings & {
  specialArticleId: string;
  isLicenseVerifiedDoctor: boolean;
  isAllowAccount: boolean;
  isMobile: boolean;
};

const HeadLogoArea = ({
  image,
  imageS3Key,
  bgColor,
  maxHeight,
}: {
  image?: string;
  imageS3Key?: string;
  bgColor: string;
  maxHeight?: number;
}) => {
  return (
    <FullWidthBox bg={bgColor} backgroundSize="cover">
      <Container>
        <Center>
          <Image
            src={image ?? generateS3DownloadUrl(imageS3Key)}
            alt="logo"
            maxH={maxHeight ?? 20}
            objectFit="contain"
          />
        </Center>
      </Container>
    </FullWidthBox>
  );
};

// 共通バナー・リンク一覧
const CommonLinkList = ({
  specialArticleId,
  links,
  isMobile,
  grid,
}: {
  specialArticleId: string;
  links: SpecialPageLinkItem[];
  isMobile: boolean;
  grid?: [number, number, number];
}) => (
  <SpecialPageLinks
    links={links}
    isMobile={isMobile}
    maxHeight="75.25px" // 一時的にTG naviと同じ幅にするための数値 スライド作成バナーが追加になった時にfigmaデザインの64pxに戻すか再検討する
    gaEventLabelPrefix={`${specialArticleId}_links`}
    grid={grid}
  />
);

// 企業関連バナー一覧
const CorporationBanners = ({
  specialArticleId,
  links,
  isMobile,
  isLicenseVerifiedDoctor,
  isAllowAccount,
  showToNoLoginUsers,
  grid,
}: {
  specialArticleId: string;
  links: SpecialPageLinkItem[];
  isMobile: boolean;
  isLicenseVerifiedDoctor: boolean;
  isAllowAccount: boolean;
  showToNoLoginUsers: boolean;
  grid?: [number, number, number];
}) =>
  isAllowAccount || isLicenseVerifiedDoctor || showToNoLoginUsers ? (
    <SpecialPageLinks
      links={links}
      isMobile={isMobile}
      maxHeight="100px"
      gaEventLabelPrefix={`${specialArticleId}_corpLinks`}
      labelJustifyContent="center"
      grid={grid}
    />
  ) : null;

// 投稿を促す見出し付きボタン
const PostButtonArea = ({
  specialArticleId,
  buttonHeader,
  tags,
  customUploadGaLabel,
}: {
  specialArticleId: string;
  buttonHeader?: string | null;
  tags?: string[];
  customUploadGaLabel?: string;
}) => {
  const uploadParam = !!tags?.length ? `?tags=${tags.join(',')}` : '';
  const buttonLabel = !!tags?.length ? (
    <>
      <Box>
        {tags.map((tag: string) => (
          <Text key={tag}>{`#${tag}`}</Text>
        ))}
      </Box>
      <Box fontSize="xs">を投稿する</Box>
    </>
  ) : (
    <Box>スライドを投稿してみる</Box>
  );
  return (
    <>
      <Box fontWeight="bold">{buttonHeader ?? 'スライド募集中！'}</Box>
      <NextLink href={`/article/upload${uploadParam}`} passHref>
        <Button
          onClick={() =>
            sendAnalyticsEvent(
              SPECIAL_PAGE_GA_CATEGORY,
              'click',
              customUploadGaLabel ?? `${specialArticleId}_upload`,
            )
          }
          as="a"
          color="white"
          bg="brand.500"
          minW={140}
          textAlign="center"
          display="flex"
          flexDirection="column"
          py={2}
          px={4}
          width="100%"
          height="auto"
          _hover={{
            opacity: '0.8',
          }}
          gap={1}
        >
          {buttonLabel}
        </Button>
      </NextLink>
      <Box fontSize="sm" whiteSpace="nowrap">
        スライド投稿方法は
        <Link href="https://note.com/antaa/n/n27364945ed02" isExternal>
          こちら
        </Link>
      </Box>
    </>
  );
};

const BoostMessage = ({
  layout = 'default',
  title,
  text,
  buttonHeader,
  tags,
  customUploadGaLabel,
  iconImageS3Key,
  caption,
  isMobile,
  specialArticleId,
  alwaysShowBoostButton,
}: SlidePostBoostContentProps['boostMessage'] & {
  isMobile: boolean;
  specialArticleId: string;
}) => {
  // プロフィール表示レイアウト
  if (layout === 'with-profile') {
    return (
      <Flex
        bgColor="backgroundMedium"
        borderRadius={4}
        py={4}
        px={6}
        gap={3}
        alignItems="center"
        justifyContent="space-between"
        flexDirection={isMobile ? 'column' : 'row'}
      >
        {iconImageS3Key && (
          <VStack flexShrink={0} alignSelf="center">
            <Image
              src={generateS3DownloadUrl(iconImageS3Key)}
              alt="寄稿者アイコン"
              w={83}
              h={83}
              rounded="full"
            />
            {caption && (
              <Box
                fontSize="sm"
                color="textGray"
                textAlign="center"
                whiteSpace="pre-wrap"
              >
                {Array.isArray(caption)
                  ? caption.map((c, index) => <Box key={index}>{c}</Box>)
                  : caption}
              </Box>
            )}
          </VStack>
        )}
        {title && (
          <Box fontWeight="bold" whiteSpace="pre-wrap">
            {title}
          </Box>
        )}
        {text && (
          <Box whiteSpace="pre-wrap">
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <Link
                    textDecoration="underline"
                    href={props.href}
                    isExternal
                    {...props}
                  />
                ),
              }}
            >
              {text}
            </ReactMarkdown>
          </Box>
        )}
        {(tags?.length || alwaysShowBoostButton) && (
          <VStack width={isMobile ? '100%' : undefined}>
            <PostButtonArea
              specialArticleId={specialArticleId}
              buttonHeader={buttonHeader}
              tags={tags}
              customUploadGaLabel={customUploadGaLabel}
            />
          </VStack>
        )}
      </Flex>
    );
  }

  // デフォルトレイアウト
  return (
    <Flex
      bgColor="backgroundMedium"
      borderRadius={4}
      py={4}
      px={6}
      gap={3}
      alignItems="center"
      justifyContent="space-between"
      flexDirection={isMobile ? 'column' : 'row'}
    >
      {title && (
        <Box fontWeight="bold" order={isMobile ? 2 : 1} whiteSpace="pre-wrap">
          {title}
        </Box>
      )}
      {text && (
        <Box order={isMobile ? 3 : 2} whiteSpace="pre-wrap">
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => (
                <Link
                  textDecoration="underline"
                  href={props.href}
                  isExternal
                  {...props}
                />
              ),
            }}
          >
            {text}
          </ReactMarkdown>
        </Box>
      )}
      {(tags?.length || alwaysShowBoostButton) && (
        <VStack order={isMobile ? 1 : 3} width={isMobile ? '100%' : undefined}>
          <PostButtonArea
            specialArticleId={specialArticleId}
            buttonHeader={buttonHeader}
            tags={tags}
            customUploadGaLabel={customUploadGaLabel}
          />
        </VStack>
      )}
    </Flex>
  );
};

// 投稿を促すメッセージと見出し付き投稿ボタン
const PostBoostArea = ({
  specialArticleId,
  boostMessage,
  description,
  isMobile,
}: {
  specialArticleId: string;
  boostMessage?: SlidePostBoostContentProps['boostMessage'] | null;
  description?: {
    title?: string | null;
    text?: string | null; // markdown形式の説明文
  };
  isMobile: boolean;
}) => (
  <Flex flexDirection="column" gap={6} flex={1}>
    {!!boostMessage && (
      <BoostMessage
        {...boostMessage}
        isMobile={isMobile}
        specialArticleId={specialArticleId}
      />
    )}
    <SpecialPageDescription {...description} />
  </Flex>
);

/**
 * 投稿促進系 特集ページテンプレート
 *
 * 企業コンテンツを表示し
 * 関連タグ等へのスライド投稿を促す
 */
export function SlidePostBoostContent({
  specialArticleId,
  isLicenseVerifiedDoctor,
  headLogo,
  isAllowAccount,
  heroContent,
  boostMessage,
  description,
  message,
  links,
  corporationBanners,
  corporationBannersGrid,
  corporationArticles,
  corporationExternalArticles,
  articles,
  hideArticlesSortButtons = false,
  hideArticlesCounts = false,
  isMobile,
  xPostIds,
}: SlidePostBoostContentProps) {
  const { articles: sortedArticles, onClickSortOrder } =
    useSlidePostBoostArticles(articles);

  const counts = calcArticlesViewLineCounts([
    ...(isAllowAccount || isLicenseVerifiedDoctor
      ? corporationArticles?.articles ?? []
      : []),
    ...(sortedArticles?.articles ?? []),
  ]);

  // 画面を表示した時に一度だけxPostIdsから一つのidをランダムに抽出する
  const xPost = useMemo(() => {
    if (!xPostIds?.length) return null;
    const randomXPostId = xPostIds[Math.floor(Math.random() * xPostIds.length)];
    return (
      <Box minW={300} flex={0}>
        <XPostWidget xPostId={randomXPostId} />
      </Box>
    );
  }, [xPostIds]);

  const commonLinkList = (
    <CommonLinkList
      specialArticleId={specialArticleId}
      links={links ?? defaultCommonLinks}
      isMobile={isMobile}
    />
  );

  const banners = (
    <>
      {isMobile ? <Divider borderColor="gray.300" my={4} /> : commonLinkList}
      {
        // 企業関連バナー
        Array.isArray(corporationBanners) ? (
          // 旧設定値の互換性確保
          <CorporationBanners
            specialArticleId={specialArticleId}
            links={corporationBanners}
            isMobile={isMobile}
            isLicenseVerifiedDoctor={isLicenseVerifiedDoctor}
            isAllowAccount={isAllowAccount}
            showToNoLoginUsers={false}
            grid={corporationBannersGrid}
          />
        ) : (
          <CorporationBanners
            specialArticleId={specialArticleId}
            links={corporationBanners?.banners ?? []}
            isMobile={isMobile}
            isLicenseVerifiedDoctor={isLicenseVerifiedDoctor}
            isAllowAccount={isAllowAccount}
            showToNoLoginUsers={!!corporationBanners?.showToNoLoginUsers}
            grid={corporationBanners?.grid}
          />
        )
      }
    </>
  );

  const corporationArticlesContent =
    isAllowAccount || isLicenseVerifiedDoctor
      ? // 企業コンテンツがarticleId指定の企業スライドの場合
        !!corporationArticles?.articles?.length && (
          <Container>
            <Box py={4}>
              <Heading as="h3" fontSize="lg" fontWeight="bold" py={2}>
                {corporationArticles.listTitle}
              </Heading>
              <ArticlesGrid
                articles={corporationArticles.articles}
                isMobile={isMobile}
                gaEventLabel={`${specialArticleId}_corpArticles`}
                onShowThumbnail={(articleId) => {
                  sendAnalyticsEvent(
                    'slide_thumbnail',
                    'show',
                    `special_${specialArticleId}#${articleId}`,
                  );
                }}
                onClick={(articleId) => {
                  sendAnalyticsEvent(
                    'slide_thumbnail',
                    'click',
                    `special_${specialArticleId}#${articleId}`,
                  );
                }}
              />
            </Box>
          </Container>
        )
      : null;

  const corporationExternalArticlesContent =
    isAllowAccount ||
    isLicenseVerifiedDoctor ||
    corporationExternalArticles?.showToNoLoginUsers
      ? // 企業コンテンツがスライドではなく外部リンクのリストの場合
        !!corporationExternalArticles?.articles?.length && (
          <Container>
            <Box py={4}>
              <Heading as="h3" fontSize="lg" fontWeight="bold" py={2}>
                {corporationExternalArticles.listTitle}
              </Heading>
              <SpecialPageLinks
                links={corporationExternalArticles?.articles}
                isMobile={isMobile}
                maxHeight="auto"
                grid={corporationExternalArticles?.grid ?? [1, 2, 3]}
                gaEventLabelPrefix={`${specialArticleId}_corpExtLinks`}
              />
            </Box>
          </Container>
        )
      : null;

  const mainArticles = sortedArticles?.articles &&
    !!sortedArticles.articles.length && (
      <>
        {sortedArticles.listTitle || sortedArticles.tags ? (
          <Heading as="h3" fontSize="lg" fontWeight="bold" py={2}>
            {sortedArticles.listTitle ??
              (sortedArticles.tags?.length &&
                `${sortedArticles?.tags
                  .map((tag) => `#${tag}`)
                  .join(' ')} に関連するスライド`)}
          </Heading>
        ) : null}
        {!hideArticlesSortButtons && (
          <Flex
            pt={2}
            pb={4}
            gap={2}
            {...(isMobile
              ? {
                  flexDirection: 'column',
                }
              : {
                  justifyContent: 'space-between',
                  alignItems: 'center',
                })}
          >
            {sortedArticles && (
              <SlideSortButtons
                current={sortedArticles.sortOrder}
                onClick={(sortOrder: SortOrder) => {
                  onClickSortOrder?.(sortOrder);
                }}
              />
            )}
            <Box fontSize="sm">{sortedArticles.articles.length}件</Box>
          </Flex>
        )}
        <ArticlesGrid
          articles={sortedArticles.articles}
          isMobile={isMobile}
          gaEventLabel={
            `${specialArticleId}_${
              sortedArticles.tags ? 'tagsearch' : 'articles'
            }` // tagsがあるときはタグ一覧、ない時はid指定とみなす
          }
          onShowThumbnail={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'show',
              `special_${specialArticleId}#${articleId}`,
            );
          }}
          onClick={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'click',
              `special_${specialArticleId}#${articleId}`,
            );
          }}
        />
      </>
    );

  return (
    <Box p={4}>
      {headLogo && <HeadLogoArea {...headLogo} />}
      <HeroArea
        {...heroContent}
        subContent={!hideArticlesCounts && <ViewLikeCount {...counts} />}
        isMobile={isMobile}
      />

      <Flex gap={4} py={4} flexDirection={isMobile ? 'column' : 'row'}>
        <PostBoostArea
          specialArticleId={specialArticleId}
          boostMessage={boostMessage ?? null}
          description={description}
          isMobile={isMobile}
        />
        {!isMobile && xPost}
      </Flex>
      {message && (
        <MessageArea
          {...message}
          specialArticleId={specialArticleId}
          isMobile={isMobile}
          textFontSize="sm"
          bgColor="backgroundMedium"
        />
      )}

      <Box py={4}>{banners}</Box>

      {corporationExternalArticlesContent || corporationArticlesContent ? (
        <FullWidthBox bg="backgroundLight">
          {corporationArticlesContent}
          {corporationExternalArticlesContent}
        </FullWidthBox>
      ) : null}

      <Box py={4}>{mainArticles}</Box>

      {
        // モバイルでは下部に表示する内容
        isMobile && (
          <>
            {xPost && (
              <>
                <Divider borderColor="gray.300" my={6} />
                {xPost}
              </>
            )}
            <Divider borderColor="gray.300" mt={6} mb={4} />
            {commonLinkList}
          </>
        )
      }
    </Box>
  );
}
